import { useMemo, useState } from "react";

import { useFormikWithoutDirtyForm } from "@shared";
import { startOfMonth, subMonths } from "date-fns";

import { ColumnFilterOption } from "@/common/components/table/ColumnFilterHeader";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { composeDetails } from "@/pages/takeover/Takeover.utils";

import { TakeoverHistoryFilterValues } from "../TakeoverHistoryPanel.types";
import { useTakeoverHistoryQuery } from "./useTakeoverHistoryQuery";

/** Default history window length. Mirrored in GetTakeoverHistoryQueryHandler (DefaultHistoryMonths) — keep in sync. */
const DEFAULT_HISTORY_MONTHS = 6;

/**
 * Page logic for the "Geschichte – Übernahmen" panel. Owns the filter form (date window + Bearbeiter:in
 * dropdowns + free-text search) and the service column filter, loads the matching history via the server
 * query, and narrows the loaded rows client-side by search text and service.
 */
export function useTakeoverHistory() {
  const [serviceFilter, setServiceFilter] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  const labels = useAppointmentLabels();

  const formik = useFormikWithoutDirtyForm<TakeoverHistoryFilterValues>({
    initialValues: {
      // Default: the first day of the month six months ago, with no upper bound (Bis empty).
      fromDate: startOfMonth(subMonths(new Date(), DEFAULT_HISTORY_MONTHS)),
      toDate: null,
      givenUpByCounselorId: null,
      takenOverByCounselorId: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: () => undefined,
  });

  const { fromDate, toDate, givenUpByCounselorId, takenOverByCounselorId } = formik.values;

  const { data, isLoading } = useTakeoverHistoryQuery({
    fromDate,
    toDate,
    givenUpByCounselorId,
    takenOverByCounselorId,
  });

  const serviceOptions = useMemo<ColumnFilterOption[]>(
    () =>
      Array.from(new Set(data.map((item) => item.purposeKey)))
        .map((purposeKey) => ({ value: purposeKey, label: labels.purposeLabelOf(purposeKey) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data, labels]
  );

  const rows = useMemo(() => {
    const search = searchText?.trim().toLowerCase() ?? "";
    return data.filter((item) => {
      if (serviceFilter !== null && item.purposeKey !== serviceFilter) {
        return false;
      }
      if (!search) {
        return true;
      }
      const haystack = [
        labels.purposeLabelOf(item.purposeKey),
        composeDetails(item, labels),
        item.givenUpByUserCode,
        item.takenOverByUserCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [data, serviceFilter, searchText, labels]);

  return { formik, rows, isLoading, serviceOptions, serviceFilter, setServiceFilter, setSearchText };
}
